import { addCalendarDays, calendarDateRange, type CalendarDate } from "../shared/calendar-date";
import { requireFinitePositive } from "../shared/validation";
import type { MacroDistribution } from "./macro-targets";
import { validateMacroDistribution } from "./macro-targets";
import type { DailyFloorPolicy } from "./daily-floor-policy";
import { calculateDailyCalorieFloor } from "./daily-floor-policy";

export type WeekPlanStatus = "active" | "completed";

export interface DailyCalorieAllocation {
  readonly date: CalendarDate;
  readonly calories: number;
  readonly isLocked: boolean;
  /** Catch-up days are allocations, not invented consumption. */
  readonly initializedAsCatchUp: boolean;
}

export interface WeekPlan {
  readonly id: string;
  readonly startDate: CalendarDate;
  readonly endDate: CalendarDate;
  readonly checkInDueDate: CalendarDate;
  readonly baselineDailyCalories: number;
  readonly weeklyCalorieBudget: number;
  readonly allocations: readonly DailyCalorieAllocation[];
  readonly fixedDailyProteinGrams: number;
  readonly macroDistribution: MacroDistribution;
  readonly dailyFloorPolicy: DailyFloorPolicy;
  readonly recommendationPolicyVersion: string;
  readonly goalPhaseId: string;
  readonly status: WeekPlanStatus;
  readonly createdAt: string;
  readonly completedAt?: string;
}

export interface CreateWeekPlanInput {
  readonly id: string;
  readonly startDate: CalendarDate;
  readonly baselineDailyCalories: number;
  readonly fixedDailyProteinGrams: number;
  readonly macroDistribution: MacroDistribution;
  readonly dailyFloorPolicy: DailyFloorPolicy;
  readonly recommendationPolicyVersion: string;
  readonly goalPhaseId: string;
  readonly now: string;
  /** Dates before/on this date receive baseline allocation but no food intake. */
  readonly catchUpThroughDate?: CalendarDate;
}

export function createWeekPlan(input: CreateWeekPlanInput): WeekPlan {
  if (!Number.isInteger(input.baselineDailyCalories)) throw new Error("Baseline daily calories must be a whole number");
  requireFinitePositive(input.baselineDailyCalories, "Baseline daily calories");
  if (!Number.isInteger(input.fixedDailyProteinGrams) || input.fixedDailyProteinGrams <= 0) {
    throw new Error("Daily protein target must be a positive whole number");
  }
  validateMacroDistribution(input.macroDistribution);
  const floor = calculateDailyCalorieFloor(input.dailyFloorPolicy, input.fixedDailyProteinGrams, input.macroDistribution);
  if (input.baselineDailyCalories < floor) throw new Error(`Baseline calories cannot be below the daily floor of ${floor}`);

  const endDate = addCalendarDays(input.startDate, 6);
  const allocations = calendarDateRange(input.startDate, endDate).map((date) => ({
    date,
    calories: input.baselineDailyCalories,
    isLocked: false,
    initializedAsCatchUp: input.catchUpThroughDate !== undefined && date <= input.catchUpThroughDate,
  }));
  return validateWeekPlan({
    id: input.id,
    startDate: input.startDate,
    endDate,
    // The nominated check-in occurs the following morning, after all seven
    // allocation days have closed. The next plan begins on this same date.
    checkInDueDate: addCalendarDays(input.startDate, 7),
    baselineDailyCalories: input.baselineDailyCalories,
    weeklyCalorieBudget: input.baselineDailyCalories * 7,
    allocations,
    fixedDailyProteinGrams: input.fixedDailyProteinGrams,
    macroDistribution: input.macroDistribution,
    dailyFloorPolicy: input.dailyFloorPolicy,
    recommendationPolicyVersion: input.recommendationPolicyVersion,
    goalPhaseId: input.goalPhaseId,
    status: "active",
    createdAt: input.now,
  });
}

export function validateWeekPlan(plan: WeekPlan): WeekPlan {
  const expectedDates = calendarDateRange(plan.startDate, plan.endDate);
  if (expectedDates.length !== 7 || plan.checkInDueDate !== addCalendarDays(plan.startDate, 7)) {
    throw new Error("A week plan must have seven allocation days followed by its check-in date");
  }
  if (plan.allocations.length !== 7) throw new Error("A week plan must contain seven allocations");
  if (!Number.isInteger(plan.fixedDailyProteinGrams) || plan.fixedDailyProteinGrams <= 0) {
    throw new Error("Daily protein target must be a positive whole number");
  }
  validateMacroDistribution(plan.macroDistribution);
  const floor = calculateDailyCalorieFloor(plan.dailyFloorPolicy, plan.fixedDailyProteinGrams, plan.macroDistribution);
  const seen = new Set<CalendarDate>();
  let total = 0;
  for (const allocation of plan.allocations) {
    if (!expectedDates.includes(allocation.date) || seen.has(allocation.date)) throw new Error("Week allocations have invalid dates");
    if (typeof allocation.isLocked !== "boolean") throw new Error("Every daily allocation lock state must be a boolean");
    if (!Number.isInteger(allocation.calories) || allocation.calories < floor) {
      throw new Error(`Every daily allocation must be a whole number at or above ${floor}`);
    }
    seen.add(allocation.date);
    total += allocation.calories;
  }
  if (total !== plan.weeklyCalorieBudget) throw new Error("Daily allocations must exactly equal the weekly calorie budget");
  return plan;
}

export function completeWeekPlan(plan: WeekPlan, completedAt: string): WeekPlan {
  if (plan.status === "completed") throw new Error("Week plan is already completed");
  return { ...plan, status: "completed", completedAt };
}
